/**
 * Meetings store - manages meeting state
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  meetingsApi,
  type Meeting,
  type MeetingDetail,
  type MeetingFilterParams,
  type CreateMeetingRequest,
  type UpdateMeetingRequest,
  type CreateAttendeeRequest,
  type TaskExtractionResponse,
  type TranscriptionProgress,
} from '@services/api/meetings';
import { useAuthStore } from '@stores/authStore';

interface MeetingsState {
  // State
  meetings: Meeting[];
  currentMeeting: MeetingDetail | null;
  isLoading: boolean;
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  filters: Omit<MeetingFilterParams, 'organizationId'>;
  selectedMeetingIds: string[];
  searchQuery: string;
  transcriptionProgress: Map<string, TranscriptionProgress>;  // meetingId -> progress
  progressPolling: Map<string, { intervalId: number; refCount: number }>;  // meetingId -> ref-counted interval

  // Actions
  fetchMeetings: (organizationId: string, page?: number) => Promise<void>;
  /** Load every meeting whose effective date falls in [start, end] (calendar view). */
  fetchCalendarMeetings: (organizationId: string, range: { start: Date; end: Date }) => Promise<void>;
  fetchMeeting: (meetingId: string) => Promise<void>;
  createMeeting: (data: CreateMeetingRequest) => Promise<Meeting>;
  updateMeeting: (meetingId: string, data: UpdateMeetingRequest) => Promise<void>;
  deleteMeeting: (meetingId: string) => Promise<void>;
  bulkDeleteMeetings: (meetingIds: string[]) => Promise<void>;
  toggleHidden: (meetingId: string, isHidden: boolean) => Promise<void>;
  bulkHideMeetings: (meetingIds: string[], isHidden: boolean, organizationId: string) => Promise<void>;
  uploadAttachment: (
    meetingId: string,
    file: File,
    onProgress?: (progress: number) => void
  ) => Promise<void>;
  retryTranscription: (meetingId: string) => Promise<void>;
  resetTranscription: (meetingId: string) => Promise<void>;
  detachRecording: (meetingId: string) => Promise<string>;
  extractTasks: (meetingId: string) => Promise<TaskExtractionResponse>;
  generateSummary: (meetingId: string) => Promise<void>;
  generateNotes: (meetingId: string, language?: string) => Promise<void>;
  updateNotes: (meetingId: string, notes: string) => Promise<void>;
  updateSpeakerMapping: (meetingId: string, speakerMapping: Record<string, string>) => Promise<void>;
  pasteTranscript: (meetingId: string, transcript: string, language?: string) => Promise<void>;
  addAttendees: (meetingId: string, attendees: CreateAttendeeRequest[]) => Promise<void>;
  removeAttendee: (meetingId: string, attendeeId: string) => Promise<void>;
  startProgressPolling: (meetingId: string) => void;
  stopProgressPolling: (meetingId: string) => void;
  updateTranscriptionProgress: (meetingId: string, progress: TranscriptionProgress) => void;
  setFilters: (filters: Partial<MeetingsState['filters']>) => void;
  setSearchQuery: (query: string) => void;
  toggleMeetingSelection: (meetingId: string) => void;
  selectAllMeetings: () => void;
  clearSelection: () => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  meetings: [] as Meeting[],
  currentMeeting: null as MeetingDetail | null,
  isLoading: false,
  isUploading: false,
  uploadProgress: 0,
  error: null as string | null,
  total: 0,
  page: 1,
  pageSize: 20,
  hasMore: false,
  filters: {} as Omit<MeetingFilterParams, 'organizationId'>,
  selectedMeetingIds: [] as string[],
  searchQuery: '',
  transcriptionProgress: new Map<string, TranscriptionProgress>(),
  progressPolling: new Map<string, { intervalId: number; refCount: number }>(),
};

/** API hard limit for page_size on GET /meetings. */
const CALENDAR_PAGE_SIZE = 100;
/** Safety cap: 10 pages × 100 = 1000 meetings in a single calendar range. */
const CALENDAR_MAX_PAGES = 10;

export const useMeetingsStore = create<MeetingsState>()(
  immer((set, get) => ({
    ...initialState,

    fetchMeetings: async (organizationId: string, page = 1) => {
      // Guard against concurrent / out-of-order "load more" calls that would
      // request the same page twice and append duplicate items.
      if (page > 1 && get().isLoading) {
        return;
      }

      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const { filters, pageSize, searchQuery } = get();
        const response = await meetingsApi.list(
          { organizationId, ...filters, search: searchQuery || undefined },
          page,
          pageSize
        );

        set((state) => {
          // Append items for subsequent pages, replace for page 1
          if (page === 1) {
            state.meetings = response.items;
          } else {
            // Dedupe against existing ids so a stray double-fetch can't duplicate rows
            const existingIds = new Set(state.meetings.map((m) => m.id));
            const newItems = response.items.filter((m) => !existingIds.has(m.id));
            state.meetings = [...state.meetings, ...newItems];
          }
          state.total = response.total;
          state.page = response.page;
          state.hasMore = response.hasMore;
          state.isLoading = false;
          state.selectedMeetingIds = [];
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch meetings';
          state.isLoading = false;
        });
      }
    },

    fetchCalendarMeetings: async (organizationId: string, range: { start: Date; end: Date }) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const { filters, searchQuery } = get();
        const rangeFilters = {
          organizationId,
          ...filters,
          search: searchQuery || undefined,
          startDate: range.start.toISOString(),
          endDate: range.end.toISOString(),
        };

        // The API caps page_size at 100; a single month rarely exceeds it, but
        // walk the pages so a busy month is never silently truncated.
        const items: Meeting[] = [];
        let page = 1;
        let total = 0;
        let hasMore = true;
        while (hasMore && page <= CALENDAR_MAX_PAGES) {
          const response = await meetingsApi.list(rangeFilters, page, CALENDAR_PAGE_SIZE);
          items.push(...response.items);
          total = response.total;
          hasMore = response.hasMore;
          page += 1;
        }

        set((state) => {
          state.meetings = items;
          state.total = total;
          state.page = 1;
          state.hasMore = false;
          state.isLoading = false;
          state.selectedMeetingIds = [];
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch meetings';
          state.isLoading = false;
        });
      }
    },

    fetchMeeting: async (meetingId: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const meeting = await meetingsApi.get(meetingId);
        set((state) => {
          state.currentMeeting = meeting;
          state.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch meeting';
          state.isLoading = false;
        });
      }
    },

    createMeeting: async (data: CreateMeetingRequest) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const meeting = await meetingsApi.create(data);
        set((state) => {
          state.meetings.unshift(meeting);
          state.total += 1;
          state.isLoading = false;
        });
        return meeting;
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to create meeting';
          state.isLoading = false;
        });
        throw error;
      }
    },

    updateMeeting: async (meetingId: string, data: UpdateMeetingRequest) => {
      try {
        const updated = await meetingsApi.update(meetingId, data);
        set((state) => {
          const index = state.meetings.findIndex((m) => m.id === meetingId);
          if (index !== -1) {
            state.meetings[index] = updated;
          }
          if (state.currentMeeting?.id === meetingId) {
            state.currentMeeting = {
              ...state.currentMeeting,
              ...updated,
              attendees: state.currentMeeting.attendees,
              attachments: state.currentMeeting.attachments,
            };
          }
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to update meeting';
        });
        throw error;
      }
    },

    deleteMeeting: async (meetingId: string) => {
      try {
        await meetingsApi.delete(meetingId);
        set((state) => {
          state.meetings = state.meetings.filter((m) => m.id !== meetingId);
          state.total -= 1;
          state.selectedMeetingIds = state.selectedMeetingIds.filter((id) => id !== meetingId);
          if (state.currentMeeting?.id === meetingId) {
            state.currentMeeting = null;
          }
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to delete meeting';
        });
        throw error;
      }
    },

    bulkDeleteMeetings: async (meetingIds: string[]) => {
      try {
        await meetingsApi.bulkDelete(meetingIds);

        // Clear current meeting if it was deleted
        const { currentMeeting } = get();
        if (currentMeeting && meetingIds.includes(currentMeeting.id)) {
          set((state) => {
            state.currentMeeting = null;
          });
        }

        // Clear selection
        set((state) => {
          state.selectedMeetingIds = [];
        });

        // Note: The caller should refetch meetings after bulk delete
        // to get fresh data and handle pagination correctly
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to delete meetings';
        });
        throw error;
      }
    },

    toggleHidden: async (meetingId: string, isHidden: boolean) => {
      try {
        await meetingsApi.toggleHidden(meetingId, isHidden);

        // Optimistic update: remove from list (hidden meetings are filtered out by default)
        set((state) => {
          state.meetings = state.meetings.filter((m) => m.id !== meetingId);
          state.total -= 1;
          state.selectedMeetingIds = state.selectedMeetingIds.filter((id) => id !== meetingId);
          if (state.currentMeeting?.id === meetingId) {
            state.currentMeeting.isHidden = isHidden;
          }
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to toggle meeting visibility';
        });
        throw error;
      }
    },

    bulkHideMeetings: async (meetingIds: string[], isHidden: boolean, organizationId: string) => {
      try {
        await meetingsApi.bulkHide(meetingIds, isHidden, organizationId);

        // Optimistic update: remove hidden meetings from list
        set((state) => {
          state.meetings = state.meetings.filter((m) => !meetingIds.includes(m.id));
          state.total -= meetingIds.length;
          state.selectedMeetingIds = [];

          // Clear current meeting if it was hidden
          if (state.currentMeeting && meetingIds.includes(state.currentMeeting.id)) {
            state.currentMeeting.isHidden = isHidden;
          }
        });

        // Note: The caller should refetch meetings after bulk hide
        // to get fresh data and handle pagination correctly
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to hide meetings';
        });
        throw error;
      }
    },

    uploadAttachment: async (
      meetingId: string,
      file: File,
      onProgress?: (progress: number) => void
    ) => {
      set((state) => {
        state.isUploading = true;
        state.uploadProgress = 0;
        state.error = null;
      });

      try {
        // Initiate upload to get presigned URL
        const { uploadUrl, attachmentId } = await meetingsApi.initiateUpload(
          meetingId,
          {
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
          }
        );

        // Upload file through API proxy
        // The backend returns a proxy URL that forwards to MinIO
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const progress = Math.round((e.loaded / e.total) * 100);
              set((state) => {
                state.uploadProgress = progress;
              });
              onProgress?.(progress);
            }
          });
          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              // Parse response for error details
              let errorDetail = '';
              try {
                const response = JSON.parse(xhr.responseText);
                errorDetail = response.detail || response.message || '';
              } catch {
                errorDetail = xhr.responseText || xhr.statusText;
              }

              // Create descriptive error message based on status
              let errorMessage = `Upload failed (${xhr.status})`;
              if (xhr.status === 401) {
                errorMessage = `Authentication failed (401): ${errorDetail || 'Please log in again'}`;
              } else if (xhr.status === 403) {
                errorMessage = `Permission denied (403): ${errorDetail || 'You do not have permission to upload'}`;
              } else if (xhr.status === 404) {
                errorMessage = `Not found (404): ${errorDetail || 'Meeting or attachment not found'}`;
              } else if (xhr.status === 413) {
                errorMessage = `File too large (413): ${errorDetail || 'The file exceeds the maximum allowed size'}`;
              } else if (xhr.status >= 500) {
                errorMessage = `Server error (${xhr.status}): ${errorDetail || 'Please try again later'}`;
              } else if (errorDetail) {
                errorMessage = `Upload failed (${xhr.status}): ${errorDetail}`;
              }

              reject(new Error(errorMessage));
            }
          });
          xhr.addEventListener('error', () => {
            reject(new Error('Upload failed: Network error - please check your connection'));
          });
          xhr.open('PUT', uploadUrl);
          xhr.setRequestHeader('Content-Type', file.type);
          // Add auth token for API proxy endpoint (get from auth store, not localStorage)
          const { accessToken } = useAuthStore.getState();
          if (accessToken) {
            xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
          } else {
            console.warn('No auth token found for upload - authentication may fail');
          }
          xhr.send(file);
        });

        // Complete upload - triggers transcription
        await meetingsApi.completeUpload(meetingId, attachmentId);

        // Refresh meeting to get updated attachment
        await get().fetchMeeting(meetingId);

        set((state) => {
          state.isUploading = false;
          state.uploadProgress = 100;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to upload file';
          state.isUploading = false;
          state.uploadProgress = 0;
        });
        throw error;
      }
    },

    retryTranscription: async (meetingId: string) => {
      set((state) => {
        state.error = null;
      });

      try {
        await meetingsApi.retryTranscription(meetingId);
        // Refresh meeting to get updated status
        await get().fetchMeeting(meetingId);
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to retry transcription';
        });
        throw error;
      }
    },

    resetTranscription: async (meetingId: string) => {
      set((state) => {
        state.error = null;
      });

      try {
        await meetingsApi.resetTranscription(meetingId);
        // Refresh meeting to get updated status (transcription cleared, status reset)
        await get().fetchMeeting(meetingId);
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to reset transcription';
        });
        throw error;
      }
    },

    detachRecording: async (meetingId: string) => {
      set((state) => {
        state.error = null;
      });

      try {
        const moved = await meetingsApi.detachRecording(meetingId);
        await get().fetchMeetings(moved.organizationId);
        return moved.id;
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to move recording';
        });
        throw error;
      }
    },

    extractTasks: async (meetingId: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const result = await meetingsApi.extractTasks(meetingId);
        set((state) => {
          state.isLoading = false;
        });
        return result;
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to extract tasks';
          state.isLoading = false;
        });
        throw error;
      }
    },

    generateSummary: async (meetingId: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const transcription = await meetingsApi.summarize(meetingId);
        set((state) => {
          if (state.currentMeeting?.id === meetingId) {
            state.currentMeeting.transcription = transcription;
          }
          state.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to generate summary';
          state.isLoading = false;
        });
        throw error;
      }
    },

    generateNotes: async (meetingId: string, language?: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const transcription = await meetingsApi.generateNotes(meetingId, language);
        set((state) => {
          if (state.currentMeeting?.id === meetingId) {
            state.currentMeeting.transcription = transcription;
          }
          state.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to generate notes';
          state.isLoading = false;
        });
        throw error;
      }
    },

    updateNotes: async (meetingId: string, notes: string) => {
      try {
        const transcription = await meetingsApi.updateNotes(meetingId, notes);
        set((state) => {
          if (state.currentMeeting?.id === meetingId) {
            // If no transcription exists, create the structure
            if (!state.currentMeeting.transcription) {
              state.currentMeeting.transcription = transcription;
            } else {
              state.currentMeeting.transcription.notes = transcription.notes;
            }
          }
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to update notes';
        });
        throw error;
      }
    },

    updateSpeakerMapping: async (meetingId: string, speakerMapping: Record<string, string>) => {
      try {
        const transcription = await meetingsApi.updateSpeakerMapping(meetingId, speakerMapping);
        set((state) => {
          if (state.currentMeeting?.id === meetingId && state.currentMeeting.transcription) {
            state.currentMeeting.transcription.speakerMapping = transcription.speakerMapping;
          }
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to update speaker mapping';
        });
        throw error;
      }
    },

    pasteTranscript: async (meetingId: string, transcript: string, language: string = 'en') => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const transcription = await meetingsApi.pasteTranscript(meetingId, transcript, language);
        set((state) => {
          if (state.currentMeeting?.id === meetingId) {
            state.currentMeeting.transcription = transcription;
            state.currentMeeting.transcriptStatus = 'completed';
          }
          state.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to save transcript';
          state.isLoading = false;
        });
        throw error;
      }
    },

    addAttendees: async (meetingId: string, attendees: CreateAttendeeRequest[]) => {
      try {
        const newAttendees = await meetingsApi.addAttendees(meetingId, attendees);
        set((state) => {
          if (state.currentMeeting?.id === meetingId) {
            state.currentMeeting.attendees.push(...newAttendees);
          }
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to add attendees';
        });
        throw error;
      }
    },

    removeAttendee: async (meetingId: string, attendeeId: string) => {
      try {
        await meetingsApi.removeAttendee(meetingId, attendeeId);
        set((state) => {
          if (state.currentMeeting?.id === meetingId) {
            state.currentMeeting.attendees = state.currentMeeting.attendees.filter(
              (a) => a.id !== attendeeId
            );
          }
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to remove attendee';
        });
        throw error;
      }
    },

    setFilters: (filters) => {
      set((state) => {
        state.filters = { ...state.filters, ...filters };
      });
    },

    setSearchQuery: (query: string) => {
      set((state) => {
        state.searchQuery = query;
      });
    },

    toggleMeetingSelection: (meetingId: string) => {
      set((state) => {
        const index = state.selectedMeetingIds.indexOf(meetingId);
        if (index === -1) {
          state.selectedMeetingIds.push(meetingId);
        } else {
          state.selectedMeetingIds.splice(index, 1);
        }
      });
    },

    selectAllMeetings: () => {
      set((state) => {
        state.selectedMeetingIds = state.meetings.map((m) => m.id);
      });
    },

    clearSelection: () => {
      set((state) => {
        state.selectedMeetingIds = [];
      });
    },

    // Progress polling
    startProgressPolling: (meetingId: string) => {
      // Already polling this meeting: another subscriber joined, ref-count up
      // so the interval survives until *every* subscriber has stopped.
      if (get().progressPolling.has(meetingId)) {
        set((state) => {
          const entry = state.progressPolling.get(meetingId);
          if (entry) {
            entry.refCount += 1;
          }
        });
        return;
      }

      const MAX_CONSECUTIVE_FAILURES = 5;
      let consecutiveFailures = 0;
      let intervalId: number;

      // Force-stop the underlying interval regardless of ref-count. Used when
      // the transcription itself is done (completed/failed) or is repeatedly
      // unreachable — subscribers' later stop calls become harmless no-ops.
      const forceStop = () => {
        window.clearInterval(intervalId);
        set((state) => {
          state.progressPolling.delete(meetingId);
        });
      };

      // Poll every 2 seconds
      intervalId = window.setInterval(async () => {
        try {
          const progress = await meetingsApi.getTranscriptionProgress(meetingId);
          consecutiveFailures = 0;
          get().updateTranscriptionProgress(meetingId, progress);

          // Auto-stop when transcription completes or fails
          if (progress.status === 'completed' || progress.status === 'failed') {
            forceStop();

            // Refresh the meeting to get the final transcription
            await get().fetchMeeting(meetingId);
          }
        } catch (error) {
          consecutiveFailures += 1;
          // Tolerate transient blips (network / 401 during token refresh); only
          // give up after several consecutive failures so a 2h GPU job doesn't
          // freeze on one hiccup.
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            console.error('Failed to fetch progress after multiple attempts, stopping polling:', error);
            forceStop();
          }
        }
      }, 2000);

      set((state) => {
        state.progressPolling.set(meetingId, { intervalId, refCount: 1 });
      });

      // Initial fetch
      meetingsApi.getTranscriptionProgress(meetingId).then((progress) => {
        get().updateTranscriptionProgress(meetingId, progress);

        // Auto-stop if completed or failed
        if (progress.status === 'completed' || progress.status === 'failed') {
          forceStop();
        }
      }).catch((error) => {
        console.error('Initial progress fetch failed:', error);
      });
    },

    stopProgressPolling: (meetingId: string) => {
      const entry = get().progressPolling.get(meetingId);
      if (!entry) {
        return;
      }

      // Only clear the interval once the last subscriber has stopped.
      if (entry.refCount <= 1) {
        window.clearInterval(entry.intervalId);
        set((state) => {
          state.progressPolling.delete(meetingId);
        });
      } else {
        set((state) => {
          const current = state.progressPolling.get(meetingId);
          if (current) {
            current.refCount -= 1;
          }
        });
      }
    },

    updateTranscriptionProgress: (meetingId: string, progress: TranscriptionProgress) => {
      set((state) => {
        state.transcriptionProgress.set(meetingId, progress);

        // Also update the meeting in the meetings list if present
        const meetingIndex = state.meetings.findIndex((m) => m.id === meetingId);
        if (meetingIndex !== -1 && state.meetings[meetingIndex]) {
          state.meetings[meetingIndex].transcriptStatus = progress.status;
        }

        // Update current meeting if it matches
        if (state.currentMeeting?.id === meetingId) {
          state.currentMeeting.transcriptStatus = progress.status;
          if (state.currentMeeting.transcription) {
            state.currentMeeting.transcription.progressPercent = progress.progressPercent ?? undefined;
            state.currentMeeting.transcription.currentPhase = progress.currentPhase ?? undefined;
            // Build phaseDetails conditionally to satisfy exactOptionalPropertyTypes
            state.currentMeeting.transcription.phaseDetails = progress.phaseDetails
              ? Object.fromEntries(
                  Object.entries({
                    message: progress.phaseDetails.message,
                    etaSeconds: progress.phaseDetails.etaSeconds,
                    updatedAt: progress.phaseDetails.updatedAt,
                  }).filter(([, value]) => value !== undefined)
                ) as { message?: string; etaSeconds?: number; updatedAt?: string }
              : undefined;
            state.currentMeeting.transcription.heartbeatAt = progress.heartbeatAt ?? undefined;
          }
        }
      });
    },

    clearError: () => {
      set((state) => {
        state.error = null;
      });
    },

    reset: () => {
      // Stop all polling intervals before resetting
      const { progressPolling } = get();
      progressPolling.forEach((entry) => {
        window.clearInterval(entry.intervalId);
      });

      set(initialState);
    },
  }))
);
